import React, { createContext, useContext, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { readSidebarCollapsed, writeSidebarCollapsed } from '@/utils/sidebarPrefs';

const SidebarContext = createContext({
  collapsed: false,
  toggleCollapsed: () => {},
});

export function useSidebar() {
  return useContext(SidebarContext);
}

export function SidebarProvider({ children, className, ...props }) {
  const [collapsed, setCollapsed] = React.useState(() => readSidebarCollapsed());

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeSidebarCollapsed(next);
      return next;
    });
  }, []);

  return (
    <SidebarContext.Provider value={{ collapsed, toggleCollapsed }}>
      <div className={cn(collapsed && 'sidebar-collapsed', className)} {...props}>
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

export function Sidebar({ className, children, ...props }) {
  const { collapsed } = useSidebar();

  return (
    <div
      className={cn(
        'app-sidebar-shell relative h-full max-h-full shrink-0 self-stretch transition-[width] duration-200 ease-in-out',
        collapsed ? 'w-[var(--sidebar-width-collapsed)]' : 'w-[var(--sidebar-width-expanded)]',
        className
      )}
    >
      <aside
        data-collapsed={collapsed ? 'true' : 'false'}
        className="app-sidebar flex h-full max-h-full w-full flex-col overflow-hidden"
        {...props}
      >
        {children}
      </aside>
      <SidebarCollapseRail />
    </div>
  );
}

export function SidebarCollapseRail({ className, ...props }) {
  const { collapsed, toggleCollapsed } = useSidebar();

  return (
    <button
      type="button"
      onClick={toggleCollapsed}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-expanded={!collapsed}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      className={cn('sidebar-collapse-rail', className)}
      {...props}
    >
      {collapsed ? (
        <ChevronRight className="sidebar-collapse-rail__icon" aria-hidden />
      ) : (
        <ChevronLeft className="sidebar-collapse-rail__icon" aria-hidden />
      )}
    </button>
  );
}

export function SidebarHeader({ className, children, ...props }) {
  const { collapsed } = useSidebar();

  return (
    <div
      className={cn(
        'flex items-center border-b',
        collapsed ? 'justify-center px-2 py-4' : 'px-6',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function SidebarContent({ className, children, ...props }) {
  const { collapsed } = useSidebar();

  return (
    <div
      className={cn(
        /* Scroll long nav if needed, but never show a second page scrollbar */
        'flex-1 overflow-x-hidden overflow-y-auto scrollbar-hide',
        collapsed ? 'px-1.5 py-3' : 'py-4',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function SidebarGroup({ className, children, ...props }) {
  const { collapsed } = useSidebar();

  return (
    <div
      className={cn(collapsed ? 'px-1 py-1' : 'px-3 py-2', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function SidebarGroupContent({ className, children, ...props }) {
  return (
    <div className={cn('space-y-1', className)} {...props}>
      {children}
    </div>
  );
}

export function SidebarMenu({ className, children, ...props }) {
  return (
    <nav className={cn('space-y-1', className)} {...props}>
      {children}
    </nav>
  );
}

export function SidebarMenuItem({ className, children, ...props }) {
  return (
    <div className={cn(className)} {...props}>
      {children}
    </div>
  );
}

export function SidebarMenuButton({ className, asChild, children, ...props }) {
  if (asChild) {
    return <>{children}</>;
  }

  return (
    <button
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
        'transition-colors hover:bg-forest-green/10 hover:text-forest-green',
        'focus:outline-none focus:ring-2 focus:ring-forest-green focus:ring-offset-2',
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/** @deprecated Prefer the built-in edge rail on `<Sidebar />`. */
export function SidebarTrigger(props) {
  return <SidebarCollapseRail {...props} />;
}
